import { AlertTriangle, Truck, Package } from "lucide-react";
import SummaryStats from "./allocation/SummaryStats";
import LoadingPlanTable from "./allocation/LoadingPlanTable";
import VehicleCard from "./allocation/VehicleCard";

function OdcAlert({ odcCaseCount, odcPermitCount }) {
  if (odcCaseCount <= 0) return null;
  return (
    <div className="border border-[#F97316]/40 bg-[#F97316]/5 p-4" data-testid="odc-alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#F97316] mt-0.5" />
        <div className="flex-1">
          <div className="heading font-semibold text-[#F97316]">
            {odcCaseCount} Over-Dimensional Cargo (ODC) case{odcCaseCount > 1 ? "s" : ""} —
            {" "}{odcPermitCount} RTO permit{odcPermitCount > 1 ? "s" : ""} needed
          </div>
          <div className="text-xs text-[#64748B] mt-1">
            Cases exceeding the standard 8×8 ft RTO passing dimensions have been
            consolidated on the minimum number of Semi Bed Trailers to reduce
            permit / challan cost. Vehicles carrying ODC are marked below.
          </div>
        </div>
      </div>
    </div>
  );
}

function UnassignedAlert({ unassigned }) {
  if (!unassigned?.length) return null;
  return (
    <div className="border border-[#EF4444]/40 bg-[#EF4444]/5 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#EF4444] mt-0.5" />
        <div className="flex-1">
          <div className="heading font-semibold text-[#EF4444]">
            {unassigned.length} case(s) could not be assigned
          </div>
          <div className="text-xs text-[#64748B] mt-1">
            These cases exceed the dimensions or weight limits of all configured vehicles.
            They likely need a specialized <strong>hydraulic axle / low-bed</strong> trailer
            (over-width cargo &gt; 275 cm, or over 35 MT weight).
          </div>
          <ul className="mt-3 space-y-1 mono text-xs">
            {unassigned.map((u, i) => (
              <li key={`unassigned-${u.case_no ?? i}-${u.description ?? ""}`} className="flex gap-3">
                <Package className="w-3.5 h-3.5 text-[#EF4444] mt-0.5" />
                <span>
                  Case {u.case_no || "—"}: {u.description} ({u.length_cm}×{u.width_cm}×
                  {u.height_cm} cm, {u.weight_kg} kg)
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function AllocationResult({ result, tripSheet }) {
  const s = result.summary || {};

  return (
    <div className="space-y-6">
      <SummaryStats summary={s} />

      {/* Trip Sheet (freight table) */}
      {tripSheet}

      <OdcAlert
        odcCaseCount={s.odc_case_count || 0}
        odcPermitCount={s.odc_permit_count || 0}
      />

      <LoadingPlanTable result={result} />

      <UnassignedAlert unassigned={result.unassigned} />

      {/* Vehicle cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {result.allocations.map((a, i) => (
          <VehicleCard key={a.id || i} alloc={a} index={i} />
        ))}
      </div>

      {result.allocations.length === 0 && (
        <div className="text-center py-10 text-[#64748B] flex flex-col items-center gap-2">
          <Truck className="w-8 h-8" />
          No vehicles allocated.
        </div>
      )}
    </div>
  );
}
